/**
 * Phase 24.4a — Tabs primitive.
 *
 * URL-state-driven via useSearchParams (mirrors Reviews.tsx pattern).
 * Active tab persists in `?tab=<key>` query param. Default tab when
 * param absent or invalid.
 *
 * Render-prop API: <Tabs ...>{(activeKey) => ...}</Tabs>
 *
 * ARIA: role="tablist" + role="tab" with aria-selected + aria-controls.
 * Keyboard: ArrowLeft/ArrowRight cycles tabs, Home/End jumps to ends.
 *
 * Usage:
 *   <Tabs
 *     tabs={[{key: "today", label: "Today"}, {key: "plan", label: "Plan"}]}
 *     defaultTab="today"
 *   >
 *     {(activeKey) => activeKey === "plan" ? <PlanTab /> : <TodayTab />}
 *   </Tabs>
 */
import { useId, useRef, type KeyboardEvent, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { cn } from "@/lib/utils";

export interface TabDef {
  key: string;
  label: string;
}

interface Props {
  tabs: TabDef[];
  defaultTab: string;
  /** Query param name. Default "tab". */
  paramName?: string;
  children: (activeKey: string) => ReactNode;
}

export function Tabs({ tabs, defaultTab, paramName = "tab", children }: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabIdPrefix = useId();
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const paramValue = searchParams.get(paramName);
  const validKeys = tabs.map((t) => t.key);
  const activeKey =
    paramValue && validKeys.includes(paramValue) ? paramValue : defaultTab;

  function setActive(key: string) {
    const next = new URLSearchParams(searchParams);
    if (key === defaultTab) {
      next.delete(paramName);
    } else {
      next.set(paramName, key);
    }
    setSearchParams(next, { replace: true });
  }

  function handleKey(e: KeyboardEvent<HTMLButtonElement>, idx: number) {
    let nextIdx: number | null = null;
    if (e.key === "ArrowRight") nextIdx = (idx + 1) % tabs.length;
    else if (e.key === "ArrowLeft") nextIdx = (idx - 1 + tabs.length) % tabs.length;
    else if (e.key === "Home") nextIdx = 0;
    else if (e.key === "End") nextIdx = tabs.length - 1;
    if (nextIdx === null) return;
    e.preventDefault();
    setActive(tabs[nextIdx].key);
    buttonRefs.current[nextIdx]?.focus();
  }

  return (
    <div data-testid="tabs-root">
      <div
        role="tablist"
        className="flex gap-1 border-b border-cream-border"
        data-testid="tabs-tablist"
      >
        {tabs.map((tab, idx) => {
          const isActive = tab.key === activeKey;
          return (
            <button
              key={tab.key}
              ref={(el) => {
                buttonRefs.current[idx] = el;
              }}
              role="tab"
              type="button"
              id={`${tabIdPrefix}-tab-${tab.key}`}
              aria-selected={isActive}
              aria-controls={`${tabIdPrefix}-panel-${tab.key}`}
              tabIndex={isActive ? 0 : -1}
              onClick={() => setActive(tab.key)}
              onKeyDown={(e) => handleKey(e, idx)}
              className={cn(
                "-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-[13px] transition-colors",
                isActive
                  ? "border-amber text-navy-deep font-medium"
                  : "border-transparent text-taupe hover:text-navy-deep",
              )}
              data-testid={`tabs-button-${tab.key}`}
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      <div
        role="tabpanel"
        id={`${tabIdPrefix}-panel-${activeKey}`}
        aria-labelledby={`${tabIdPrefix}-tab-${activeKey}`}
        data-testid={`tabs-panel-${activeKey}`}
        className="pt-4"
      >
        {children(activeKey)}
      </div>
    </div>
  );
}
